const KL_CORPORATE_GIFTING_FORM_SELECTOR = '[data-kl-corporate-gifting-form-form]';
const KL_CORPORATE_GIFTING_FORM_FIELD_SELECTOR = '[data-kl-corporate-gifting-form-field]';
const KL_CORPORATE_GIFTING_FORM_SUBMIT_SELECTOR = '[data-kl-corporate-gifting-form-submit]';
const KL_CORPORATE_GIFTING_FORM_MESSAGE_SELECTOR = '[data-kl-corporate-gifting-form-message]';

const KL_CORPORATE_GIFTING_FORM_EMAIL_PATTERN = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
const KL_CORPORATE_GIFTING_FORM_PHONE_PATTERN = /^[0-9]{7,15}$/;

function getFieldErrorMessage(fieldName, values) {
  if (fieldName === 'name') {
    if (!values.name.trim()) return 'Name is required.';
    if (/[0-9]/.test(values.name)) return 'Name cannot contain numbers.';
    return '';
  }
  if (fieldName === 'company') return values.company.trim() ? '' : 'Company is required.';

  if (fieldName === 'email') {
    if (!values.email.trim()) return 'Email is required.';
    return KL_CORPORATE_GIFTING_FORM_EMAIL_PATTERN.test(values.email.trim()) ? '' : 'Please enter a valid email address.';
  }

  /*
    One message for both an empty box and a malformed number, which is what the reference
    says in both cases. The other four fields do distinguish them — "Email is required."
    against "Please enter a valid email address." — and a phone number is the one field where
    the distinction does not help: a visitor who typed four digits and a visitor who typed
    none are both being told the same thing, that this is not yet a phone number.
  */
  if (fieldName === 'phone') {
    return KL_CORPORATE_GIFTING_FORM_PHONE_PATTERN.test(values.phone.replace(/[^0-9]/g, ''))
      ? ''
      : 'Please enter a valid phone number.';
  }

  if (fieldName === 'description') return values.description.trim() ? '' : 'Description is required.';

  return '';
}

function getFormValues(form) {
  const fields = form.querySelectorAll(KL_CORPORATE_GIFTING_FORM_FIELD_SELECTOR);
  const values = { name: '', company: '', email: '', phoneCountryCode: '+1', phone: '', description: '' };

  fields.forEach((field) => {
    const fieldName = field.dataset.klCorporateGiftingFormField;
    if (fieldName in values) values[fieldName] = field.value;
  });

  return values;
}

function getFormErrors(values) {
  return {
    name: getFieldErrorMessage('name', values),
    company: getFieldErrorMessage('company', values),
    email: getFieldErrorMessage('email', values),
    phone: getFieldErrorMessage('phone', values),
    description: getFieldErrorMessage('description', values),
  };
}

function renderFieldErrors(form, errors) {
  Object.keys(errors).forEach((fieldName) => {
    const errorElement = form.querySelector(`[data-kl-corporate-gifting-form-field-error="${fieldName}"]`);
    const field = form.querySelector(`[data-kl-corporate-gifting-form-field="${fieldName}"]`);
    const wrapper = field ? field.closest('.kl-corporate-gifting-form__field') : null;
    const message = errors[fieldName];

    if (errorElement) {
      errorElement.textContent = message;
      errorElement.hidden = !message;
    }

    if (wrapper) wrapper.classList.toggle('kl-corporate-gifting-form__field--is-invalid', Boolean(message));
  });
}

/*
  The "all form fields mandatory" line above the button. A complaint, not a standing notice:
  it appears when a submit is refused and goes as soon as the form is acceptable — the same
  behaviour assets/kl-careers-form.js gives the identical line on the careers form.
*/
function setMandatoryVisible(form, isVisible) {
  const mandatoryElement = form.querySelector('[data-kl-corporate-gifting-form-mandatory]');

  if (!mandatoryElement) return;

  mandatoryElement.hidden = !isVisible;
}

function renderFormMessage(form, text, tone) {
  const messageElement = form.querySelector(KL_CORPORATE_GIFTING_FORM_MESSAGE_SELECTOR);

  if (!messageElement) return;

  messageElement.textContent = text;
  messageElement.hidden = !text;
  messageElement.classList.toggle('kl-corporate-gifting-form__message--success', tone === 'success');
  messageElement.classList.toggle('kl-corporate-gifting-form__message--error', tone === 'error');
}

async function submitFormValues(form, values) {
  const endpointUrl = form.dataset.klCorporateGiftingFormEndpoint;

  if (!endpointUrl) {
    console.debug('kl-corporate-gifting-form: no submit endpoint is configured on this section');
    return false;
  }

  const response = await fetch(endpointUrl, {
    method: 'POST',
    headers: {
      Accept: 'application/json',
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({
      to_emails: [form.dataset.klCorporateGiftingFormRecipient || 'b2b@karmaandluck.com'],
      phone_no: `${values.phoneCountryCode}${values.phone.trim()}`,
      email: values.email.trim(),
      company: values.company.trim(),
      name: values.name.trim(),
      description: values.description.trim(),
    }),
  });

  return response.ok;
}

async function handleFormSubmit(event) {
  const form = event.currentTarget;
  const submitButton = form.querySelector(KL_CORPORATE_GIFTING_FORM_SUBMIT_SELECTOR);

  event.preventDefault();

  const values = getFormValues(form);
  const errors = getFormErrors(values);

  renderFieldErrors(form, errors);

  const isValid = Object.values(errors).every((message) => !message);

  setMandatoryVisible(form, !isValid);

  if (!isValid) {
    renderFormMessage(form, '', null);
    return;
  }

  if (submitButton) submitButton.disabled = true;
  renderFormMessage(form, '', null);

  try {
    const isSuccessful = await submitFormValues(form, values);

    if (isSuccessful) {
      renderFormMessage(form, form.dataset.klCorporateGiftingFormSuccess || 'Sent successfully!', 'success');
      form.reset();
      setMandatoryVisible(form, false);
    } else {
      renderFormMessage(form, form.dataset.klCorporateGiftingFormError || 'Something went wrong. Please try again.', 'error');
    }
  } catch (error) {
    console.debug('kl-corporate-gifting-form: the submit request failed', { error });
    renderFormMessage(form, form.dataset.klCorporateGiftingFormError || 'Something went wrong. Please try again.', 'error');
  } finally {
    if (submitButton) submitButton.disabled = false;
  }
}

function validateSingleField(form, field) {
  const fieldName = field.dataset.klCorporateGiftingFormField;
  if (!fieldName || fieldName === 'phoneCountryCode') return;

  const values = getFormValues(form);
  const errorMessage = getFieldErrorMessage(fieldName, values);
  const errorElement = form.querySelector(`[data-kl-corporate-gifting-form-field-error="${fieldName}"]`);
  const wrapper = field.closest('.kl-corporate-gifting-form__field');

  if (errorElement) {
    errorElement.textContent = errorMessage;
    errorElement.hidden = !errorMessage;
  }

  if (wrapper) {
    wrapper.classList.toggle('kl-corporate-gifting-form__field--is-invalid', Boolean(errorMessage));
  }

  /*
    Clear the mandatory line the moment nothing is left to complain about, rather than
    leaving it up until the next submit. It is answering a refusal, and once every field is
    acceptable there is no refusal left for it to answer.
  */
  const isFormValid = Object.values(getFormErrors(values)).every((message) => !message);

  if (isFormValid) setMandatoryVisible(form, false);
}

function initCorporateGiftingForm(form) {
  if (!form || form.dataset.klCorporateGiftingFormReady === 'true') return;

  form.dataset.klCorporateGiftingFormReady = 'true';
  form.addEventListener('submit', handleFormSubmit);

  const fields = form.querySelectorAll(KL_CORPORATE_GIFTING_FORM_FIELD_SELECTOR);
  fields.forEach((field) => {
    const fieldName = field.dataset.klCorporateGiftingFormField;

    if (fieldName === 'name') {
      field.addEventListener('input', (event) => {
        event.target.value = event.target.value.replace(/[0-9]/g, '');
        validateSingleField(form, field);
      });
    } else if (fieldName === 'phone') {
      field.addEventListener('input', (event) => {
        event.target.value = event.target.value.replace(/[^0-9]/g, '');
        validateSingleField(form, field);
      });
    } else {
      field.addEventListener('input', () => {
        const wrapper = field.closest('.kl-corporate-gifting-form__field');
        if (wrapper && wrapper.classList.contains('kl-corporate-gifting-form__field--is-invalid')) {
          validateSingleField(form, field);
        }
      });
    }

    field.addEventListener('blur', () => {
      validateSingleField(form, field);
    });
  });
}

document.querySelectorAll(KL_CORPORATE_GIFTING_FORM_SELECTOR).forEach(initCorporateGiftingForm);

document.addEventListener('shopify:section:load', (event) => {
  event.target.querySelectorAll(KL_CORPORATE_GIFTING_FORM_SELECTOR).forEach(initCorporateGiftingForm);
});
